import analyticsAction from "../analytics/analytics-action"
import analyticsError from "../analytics/analytics-error"
import analyticsFormSubmit from "../analytics/analytics-form-submit"
import getCookieConsentPreferences from "../../utils/cookie-consent"
declare global {
  interface Window {
    hubspotFormIsLoading?: boolean
    hubspotFormPromise?: Promise<void>
    hbspt: {
      forms: {
        create: (setup: unknown) => void
      }
    }
  }
}

interface Props {
  formId: string
  sfdcCampaignId: string | null
  formInstanceId: string
  errorMessageClass: string
  cssClass: string
  submitButtonClass: string
  pageTheme: string
}

const hsFormSrc = "https://js.hsforms.net/forms/v2.js"

async function hubspotForms(
  {},
  {
    formId,
    sfdcCampaignId,
    formInstanceId,
    errorMessageClass,
    cssClass,
    submitButtonClass,
    pageTheme,
  }: Props,
) {
  try {
    await loadHubspotFormScript()

    window.hbspt.forms.create({
      portalId: "516278",
      formId: formId,

      // Salesforce campaign id
      sfdcCampaignId: sfdcCampaignId,

      // If same form appears >1 per page. As unique formInstanceId is required
      formInstanceId: formInstanceId,
      target: `#hsform-${formInstanceId}`,

      // Removes Hubspot CSS
      css: "",
      cssRequired: "",

      // Additional input error message class eg. colour
      errorMessageClass: errorMessageClass,

      // Additional subscribe form classes added to <form> element
      cssClass: `hubspot-form ${cssClass}`,

      // Add custom button classes
      submitButtonClass: submitButtonClass,

      onFormReady: function ($form: HTMLFormElement) {
        // Handle whether form is visible or in a hidden dialog
        const isVisible = $form.checkVisibility()

        if (isVisible) {
          analyticsAction("Form loaded", {
            "Form ID": formId,
            "Form provider": "hubspot",
          })
        } else {
          const formDialog = document.getElementById(`dialog-for-${formId}`)
          formDialog?.addEventListener(
            "formDialogOpened",
            e => {
              if ((e as CustomEvent).detail === `dialog-for-${formId}`) {
                analyticsAction("Form loaded", {
                  "Form ID": formId,
                  "Form provider": "hubspot",
                })
              }
            },
            { once: true },
          )
        }

        // Set hidden fields for form consolidation test\
        setHiddenFields($form, pageTheme)

        // Hubspot Hidden Country Field
        setHiddenCountryField($form)
      },

      onFormSubmitted: function (
        _$form: HTMLFormElement,
        data: {
          redirectUrl?: string
          submissionValues: Record<string, string>
        },
      ) {
        analyticsFormSubmit(formId, data)
      },
    })
  } catch (error) {
    if (error instanceof Error) {
      await analyticsError(`Hubspot form`, error.message)
    }
  }
}

function setHiddenCountryField($form: HTMLFormElement) {
  const dropdown = $form.querySelector<HTMLSelectElement>(
    ".hs-fieldtype-intl-phone select",
  )
  const hiddenField = $form.querySelector<HTMLInputElement>(
    "input[name='country___phone_number']",
  )

  if (dropdown && hiddenField) {
    hiddenField.value = dropdown?.options[dropdown.selectedIndex].value
    dropdown.addEventListener("change", () => {
      const selectedOption = dropdown.options[dropdown.selectedIndex]
      hiddenField.value = selectedOption.value
    })
  }
}

function setHiddenFields($form: HTMLFormElement, pageTheme: string) {
  const hiddenURLField = $form.querySelector<HTMLInputElement>(
    "input[name='page_url']",
  )
  const hiddenThemeField = $form.querySelector<HTMLInputElement>(
    "input[name='form_asset_topic']",
  )
  const hiddenAdOptOutField = $form.querySelector<HTMLInputElement>(
    "input[name='ad_opt_out']",
  )
  const hiddenDirectoryField = $form.querySelector<HTMLInputElement>(
    "input[name='page_url_directory']",
  )

  if (hiddenURLField) {
    hiddenURLField.value = window.location.origin + window.location.pathname
  }

  if (hiddenThemeField) {
    hiddenThemeField.value = pageTheme
  }

  if (hiddenDirectoryField) {
    const defaultDirectory = window.location.pathname.split("/")[1] || "home"
    hiddenDirectoryField.value =
      defaultDirectory === "p" ? "paid" : defaultDirectory
  }

  if (hiddenAdOptOutField) {
    const consentSettings = getCookieConsentPreferences()
    const optOut = consentSettings?.ad_storage === "denied" ? "true" : "false"
    hiddenAdOptOutField.value = optOut
  }
}

async function loadHubspotFormScript() {
  if (window.hubspotFormIsLoading) {
    if (window.hubspotFormPromise) {
      await window.hubspotFormPromise
    }
  } else {
    if (!window.hubspotFormPromise) {
      window.hubspotFormIsLoading = true

      window.hubspotFormPromise = new Promise<void>((resolve, reject) => {
        const scriptEl = document.createElement("script")
        scriptEl.src = hsFormSrc
        scriptEl.onload = () => {
          window.hubspotFormIsLoading = false
          resolve()
        }
        scriptEl.onerror = () => {
          window.hubspotFormIsLoading = false
          reject(new Error("Hubspot Forms not loaded."))
        }
        document.head.appendChild(scriptEl)
      })

      await window.hubspotFormPromise
    }
  }
}

export default hubspotForms
